"use client"

import React, { useState, useEffect, useRef } from "react"
import {
  FiX,
  FiClock,
  FiUser,
  FiFileText,
  FiCheckCircle,
  FiAlertCircle,
  FiCalendar,
  FiDownload,
  FiChevronDown,
  FiChevronUp,
  FiChevronLeft,
  FiChevronRight,
  FiMapPin,
  FiStar,
  FiImage,
  FiEye,
  FiMaximize2,
  FiMinimize2,
  FiRotateCw,
  FiExternalLink,
  FiTrash2,
  FiUpload,
} from "react-icons/fi"

import {
  FaFilePdf,
  FaFileExcel,
  FaFileWord,
  FaFilePowerpoint,
} from "react-icons/fa"
import axiosInstance from "../../../libs/axios"
import { uploadFilesWithPresignedPayload } from "../../../libs/presigned-upload"
import { formatDate } from "."
import { toast } from "react-hot-toast"
import { useAuth } from "../../../provider/auth.provider"
import { useConfirm } from "../../../provider/confirm.provider"

const Video: any = 'video'

interface StepDetailsOffcanvasProps {
  isOpen: boolean
  onClose: () => void
  siteId: number
  status: string
}

interface User {
  id: number
  username: string
  first_name: string
  last_name: string
  email: string
}

interface FileUpload {
  id: number
  file_name: string
  file_path: string
  file_size: number | string
  mime_type: string
  file_type: string
  status: string
  uploaded_by: User
  uploaded_at: string
  approved_by?: User
  approved_at?: string
  rejected_by?: User
  rejected_at?: string
  rejection_reason?: string
}

interface WorkflowLog {
  id: number
  action_type: string
  performed_at: string
  remarks: string
  performer: User
}

interface TimelineStep {
  step: string
  status: string
  stepStatus: string
  assignedTo?: User
  assignedBy?: User
  completedBy?: User
  reviewedBy?: User
  rejectedBy?: User
  workingBy?: User
  uploadedBy?: User
  sentBy?: User
  finalizedBy?: User
  startedAt?: string
  assignedAt?: string
  completedAt?: string
  reviewedAt?: string
  rejectedAt?: string
  uploadedAt?: string
  sentAt?: string
  finalizedAt?: string
  remarks?: string
  visitRemarks?: string
  qualityFeedback?: string
  rating?: number
  files: Record<string, FileUpload[]>
  logs: WorkflowLog[]
  rejectionReason?: string
  rejectionType?: string
  rejectionDetails?: string
  rejectionCount?: number
  isResolved?: boolean
  resolvedBy?: User
  resolvedAt?: string
  resolutionNotes?: string
  details?: any
  feedbackToEngineer?: any
  feedbackToReportMaker?: any
  clientDetails?: any
  priority?: string
  workStatus?: string
  visitStatus?: string
  sentBackTo?: string
}

interface ApiResponse {
  site_info: any
  requested_step: TimelineStep
  previous_steps: TimelineStep[]
  complete_timeline: TimelineStep[]
  summary: {
    total_steps: number
    completed_steps: number
    rejected_steps: number
    pending_steps: number
    in_progress_steps: number
    total_files: number
    total_rejections: number
    total_logs: number
    current_status: string
    requested_status: string
  }
  participants: {
    site_engineer?: User
    ta?: User
    mis?: User
    report_maker?: User
    validator?: User
  }
  all_files_by_stage: Record<string, FileUpload[]>
  all_rejections: any[]
}

export const StepDetailsOffcanvas: React.FC<StepDetailsOffcanvasProps> = ({
  isOpen,
  onClose,
  siteId,
  status
}) => {
  const { confirm } = useConfirm()
  const MIN_IMAGE_ZOOM = 1
  const MAX_IMAGE_ZOOM = 10
  const IMAGE_ZOOM_STEP = 0.5
  const DOUBLE_CLICK_ZOOM_LEVEL = 2
  const [apiData, setApiData] = useState<ApiResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set())
  const [activeTab, setActiveTab] = useState<'current' | 'previous'>('current')
  const [activeFileTab, setActiveFileTab] = useState<'images' | 'videos' | 'documents'>('images')
  const [fileTabInitialized, setFileTabInitialized] = useState(false)
  const [previewFile, setPreviewFile] = useState<FileUpload | null>(null)
  const [previewCollection, setPreviewCollection] = useState<FileUpload[]>([])
  const [imageZoom, setImageZoom] = useState(MIN_IMAGE_ZOOM)
  const [imageRotation, setImageRotation] = useState(0)
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false)
  const previewContainerRef = useRef<HTMLDivElement>(null)
  const [isDownloadingZip, setIsDownloadingZip] = useState(false)
  const [downloadingGroup, setDownloadingGroup] = useState<string | null>(null)
  const [correctionStage, setCorrectionStage] = useState<string>("")
  const [correctionFiles, setCorrectionFiles] = useState<File[]>([])
  const [correctionUploading, setCorrectionUploading] = useState(false)
  const { hasPermission } = useAuth()

  // Gallery upload permissions: Check for specific sub-permissions
  // canAddMoreFiles: User can upload using "Add More Files" option
  const canAddMoreFiles = hasPermission("Site", "add_more_files") || hasPermission("Site", "mis")
  
  // canUploadOthers: User can upload using "Others" option
  const canUploadOthers = hasPermission("Site", "upload_others") || hasPermission("Site", "mis")
  
  // canShowUploadOptions: Show upload UI if user has at least one upload permission
  const canShowUploadOptions = canAddMoreFiles || canUploadOthers

  // Legacy: correction upload restricted to report stages for MIS
  const canCorrectReportFiles = (canAddMoreFiles || canUploadOthers) && ["report_completed", "report_finalized"].includes(status)
  


  useEffect(() => {
    if (isOpen && status) {
      fetchStepDetails()
      setExpandedSteps(new Set([0]))
      setFileTabInitialized(false)
      setActiveFileTab('images')
    }
  }, [isOpen, status, siteId])

 

  const handleDownloadZip = async (description?: string) => {
    if (isDownloadingZip) return
    setIsDownloadingZip(true)
    setDownloadingGroup(description || "__all__")
    try {
      const now = new Date()
      const pad2 = (n: number) => String(n).padStart(2, "0")
      const datePart = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`
      const timePart = `${pad2(now.getHours())}-${pad2(now.getMinutes())}`
      const siteLabel = apiData?.site_info?.site_id || String(siteId)
      const sanitizedDescription = description
        ? description.replace(/[^a-zA-Z0-9_-]+/g, "-").replace(/^-+|-+$/g, "")
        : ""
      const zipName = `${siteLabel}${sanitizedDescription ? `_${sanitizedDescription}` : ""}_${datePart}_${timePart}.zip`

      // Fetch presigned URLs from backend (bypasses CORS on direct S3 access)
      const presignedResp = await axiosInstance.get(
        `/survey/sites/${siteId}/${status}/presigned-urls`,
        {
          params: description ? { description } : undefined,
        }
      )
      const fileEntries: Array<{ url: string; fileName: string }> =
        presignedResp.data?.data?.files || []

      if (!fileEntries.length) {
        toast.error("No files to download")
        return
      }

      const JSZipModule = await import("jszip")
      const JSZip = JSZipModule.default
      const zip = new JSZip()

      const usedNames = new Set<string>()

      const indexToLetters = (index: number): string => {
        let n = index
        let out = ""
        while (true) {
          out = String.fromCharCode(97 + (n % 26)) + out
          n = Math.floor(n / 26) - 1
          if (n < 0) break
        }
        return out || "a"
      }

      const makeUniqueZipEntryName = (desiredName: string): string => {
        const safeName = desiredName.split(/[\\/]/).pop() || "file"
        const key = safeName.toLowerCase()
        if (!usedNames.has(key)) {
          usedNames.add(key)
          return safeName
        }
        const dotIdx = safeName.lastIndexOf(".")
        const ext = dotIdx >= 0 ? safeName.slice(dotIdx) : ""
        const base = dotIdx >= 0 ? safeName.slice(0, dotIdx) : safeName
        let suffixIndex = 0
        while (true) {
          const suffix = indexToLetters(suffixIndex)
          const candidate = `${base}_${suffix}${ext}`
          const candidateKey = candidate.toLowerCase()
          if (!usedNames.has(candidateKey)) {
            usedNames.add(candidateKey)
            return candidate
          }
          suffixIndex++
        }
      }

      // Pre-compute deterministic ZIP entry names in order
      const zipEntryNames = fileEntries.map((f) => makeUniqueZipEntryName(f.fileName))

      // Fetch all files via presigned URLs (no CORS issues) and add to zip
      await Promise.all(
        fileEntries.map(async (file, idx) => {
          try {
            const resp = await fetch(file.url)
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
            const blob = await resp.blob()
            zip.file(zipEntryNames[idx], blob)
          } catch (err) {
            console.error(`Skipping file ${file.fileName}:`, err)
          }
        })
      )

      const content = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 9 },
      })

      const blobUrl = window.URL.createObjectURL(content)
      const link = document.createElement("a")
      link.href = blobUrl
      link.download = zipName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error("Error creating zip:", error)
      toast.error("Failed to create ZIP")
    } finally {
      setIsDownloadingZip(false)
      setDownloadingGroup(null)
    }
  }

  const fetchStepDetails = async () => {
    setLoading(true)
    try {
      const response = await axiosInstance.get(`/survey/sites/${siteId}/steps/${status}`)
      const data = response.data.data
      console.log("API Response:", data)
      setApiData(data)
    } catch (error) {
      console.error("Error fetching step details:", error)
    } finally {
      setLoading(false)
    }
  }

  const toggleStepExpansion = (index: number) => {
    const newExpanded = new Set(expandedSteps)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedSteps(newExpanded)
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB"
    return (bytes / (1024 * 1024)).toFixed(2) + " MB"
  }

  const handleDownloadFile = async (fileUrl: string, fileName: string) => {
    try {
      const isAbsoluteUrl = /^https?:\/\//i.test(fileUrl)
      const response = await axiosInstance.get(fileUrl, {
        responseType: "blob",
        baseURL: isAbsoluteUrl ? "" : undefined,
      })

      const blob = response.data
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = url
      link.download = fileName || "download"
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Error downloading file:", error)
      window.open(fileUrl, "_blank")
    }
  }

  const handleDeleteReportFile = async (file: FileUpload) => {
    const ok = await confirm({
      title: "Remove file",
      message: `Remove ${file.file_name}? This cannot be undone.`,
      confirmText: "Remove file",
      variant: "danger",
    })
    if (!ok) return
    try {
      await axiosInstance.delete(`/survey/sites/${siteId}/report-stage-files/${file.id}`)
      toast.success("File removed")
      fetchStepDetails()
    } catch (error) {
      console.error("Error removing report file:", error)
      toast.error("Failed to remove file")
    }
  }

  const handleUploadCorrectionFiles = async () => {
    if (!correctionStage || correctionFiles.length === 0) return
    setCorrectionUploading(true)
    try {
      const uploadedFiles = await uploadFilesWithPresignedPayload(
        correctionFiles.map((file) => ({ file })),
        {
          siteId,
          folderName: correctionStage === "report_finalized"
            ? "project_sites/validator-files"
            : "project_sites/reports",
        }
      )
      await axiosInstance.post(`/survey/sites/${siteId}/report-stage-files`, {
        upload_stage: correctionStage,
        uploadedFiles,
      })
      toast.success("Correct file uploaded")
      setCorrectionFiles([])
      setCorrectionStage("")
      fetchStepDetails()
    } catch (error) {
      console.error("Error uploading correction files:", error)
      toast.error("Failed to upload correction files")
    } finally {
      setCorrectionUploading(false)
    }
  }

  const isImageFile = (file: FileUpload) => {
    if (file.mime_type?.startsWith("image/")) return true
    return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(file.file_name)
  }

  const isVideoFile = (file: FileUpload) => {
    if (file.mime_type?.startsWith("video/")) return true
    return /\.(mp4|mov|m4v|avi|mkv|webm)$/i.test(file.file_name)
  }

  // Checking File Type
  const isPdfFile = (file: FileUpload) => {
    if (file.mime_type === "application/pdf") return true
    return /\.pdf$/i.test(file.file_name)
  }

  const isExcelFile = (file: FileUpload) => {
    if (file.mime_type?.includes("spreadsheet") || file.mime_type === "application/vnd.ms-excel") return true
    return /\.(xlsx|xls|xlsm)$/i.test(file.file_name)
  }

  const isWordFile = (file: FileUpload) => {
    if (file.mime_type?.includes("wordprocessingml") || file.mime_type === "application/msword") return true
    return /\.(docx|doc)$/i.test(file.file_name)
  }

  const isPowerPointFile = (file: FileUpload) => {
    if (file.mime_type?.includes("presentationml") || file.mime_type === "application/vnd.ms-powerpoint") return true
    return /\.(pptx|ppt)$/i.test(file.file_name)
  }

  const getFileTypeIcon = (file: FileUpload) => {
    if (isImageFile(file)) {
      return <FiImage className="text-blue-500" size={18} />
    }
    // if (isVideoFile(file)) {
    //   return <FiVideo className="text-purple-500" size={18} />
    // }
    if (isPdfFile(file)) {
      return <FaFilePdf className="text-red-500" size={18} />
    }
    if (isExcelFile(file)) {
      return <FaFileExcel className="text-green-500" size={18} />
    }
    if (isWordFile(file)) {
      return <FaFileWord className="text-blue-600" size={18} />
    }
    if (isPowerPointFile(file)) {
      return <FaFilePowerpoint className="text-orange-500" size={18} />
    }
    return <FiFileText className="text-gray-400" size={16} />
  }

  const handlePreviewFile = (file: FileUpload, collection?: FileUpload[]) => {
    if (isImageFile(file) || isVideoFile(file)) {
      setPreviewFile(file)
      setPreviewCollection(collection || [])
      setImageZoom(MIN_IMAGE_ZOOM)
      return
    }

    // For documents/other types, open in a new tab
    if (file.file_path) {
      window.open(file.file_path, "_blank")
    }
  }

  const closePreview = () => {
    setPreviewFile(null)
    setPreviewCollection([])
    setImageZoom(MIN_IMAGE_ZOOM)
    setImageRotation(0)
    if (document.fullscreenElement) document.exitFullscreen()
    setIsPreviewFullscreen(false)
  }

  const handleRotate = () => setImageRotation(prev => (prev + 90) % 360)

  const togglePreviewFullscreen = () => {
    if (!document.fullscreenElement && previewContainerRef.current) {
      previewContainerRef.current.requestFullscreen().then(() => setIsPreviewFullscreen(true))
    } else if (document.fullscreenElement) {
      document.exitFullscreen().then(() => setIsPreviewFullscreen(false))
    }
  }

  const handlePreviewNavigation = (direction: "prev" | "next") => {
    if (!previewFile || previewCollection.length === 0) return
    const currentIndex = previewCollection.findIndex((file) => file.id === previewFile.id)
    if (currentIndex === -1) return

    const nextIndex =
      direction === "next"
        ? (currentIndex + 1) % previewCollection.length
        : (currentIndex - 1 + previewCollection.length) % previewCollection.length

    setPreviewFile(previewCollection[nextIndex])
    setImageZoom(MIN_IMAGE_ZOOM)
    setImageRotation(0)
  }

  const handleZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + IMAGE_ZOOM_STEP, MAX_IMAGE_ZOOM))
  }

  const handleZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - IMAGE_ZOOM_STEP, MIN_IMAGE_ZOOM))
  }

  const handleImageDoubleClick = () => {
    setImageZoom((prev) => (prev > MIN_IMAGE_ZOOM ? MIN_IMAGE_ZOOM : DOUBLE_CLICK_ZOOM_LEVEL))
  }

  const getStatusBadge = (stepStatus: string) => {
    switch (stepStatus) {
      case "completed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FiCheckCircle className="mr-1" size={12} />
            Completed
          </span>
        )
      case "in_progress":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <FiClock className="mr-1" size={12} />
            In Progress
          </span>
        )
      case "rejected":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <FiAlertCircle className="mr-1" size={12} />
            Rejected
          </span>
        )
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <FiClock className="mr-1" size={12} />
            Pending
          </span>
        )
      case "assigned":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <FiUser className="mr-1" size={12} />
            Assigned
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {stepStatus.replace(/_/g, " ")}
          </span>
        )
    }
  }

  const renderStepCard = (step: TimelineStep, index: number, isCurrent: boolean = false) => {
    const isExpanded = expandedSteps.has(index)

    return (
      <div
        key={index}
        className={`border rounded overflow-hidden transition-all ${isCurrent ? 'border-blue-500 bg-blue-50/30' : 'border-gray-200 bg-white'
          }`}
      >
        {/* Step Header */}
        <button
          onClick={() => toggleStepExpansion(index)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.stepStatus === 'completed' ? 'bg-green-500 text-white' :
              step.stepStatus === 'rejected' ? 'bg-red-500 text-white' :
                step.stepStatus === 'in_progress' ? 'bg-yellow-500 text-white' :
                  'bg-gray-300 text-white'
              }`}>
              {index + 1}
            </div>
            <div className="text-left">
              <h4 className={`font-medium ${isCurrent ? 'text-blue-900' : 'text-gray-900'}`}>
                {step.step}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                {getStatusBadge(step.stepStatus)}
                {isCurrent && (
                  <span className="text-xs text-blue-600 font-medium">CURRENT STEP</span>
                )}
              </div>
            </div>
          </div>
          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
        </button>

        {/* Step Details */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4 space-y-4">
            {/* User Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {step.assignedTo && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-500">Assigned To</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-medium">
                      {step.assignedTo.first_name[0]}{step.assignedTo.last_name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{step.assignedTo.first_name} {step.assignedTo.last_name}</p>
                      <p className="text-xs text-gray-500">@{step.assignedTo.username}</p>
                    </div>
                  </div>
                </div>
              )}

              {step.completedBy && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-500">Completed By</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-medium">
                      {step.completedBy.first_name?.[0]}{step.completedBy.last_name?.[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {step.completedBy.first_name} {step.completedBy.last_name}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {step.reviewedBy && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-500">Reviewed By</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-medium">
                      {step.reviewedBy.first_name[0]}{step.reviewedBy.last_name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{step.reviewedBy.first_name} {step.reviewedBy.last_name}</p>
                      <p className="text-xs text-gray-500">@{step.reviewedBy.username}</p>
                    </div>
                  </div>
                </div>
              )}

              {step.rejectedBy && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-500">Rejected By</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-medium">
                      {step.rejectedBy.first_name[0]}{step.rejectedBy.last_name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{step.rejectedBy.first_name} {step.rejectedBy.last_name}</p>
                      <p className="text-xs text-gray-500">@{step.rejectedBy.username}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {step.assignedAt && (
                <div className="flex items-center gap-2 text-sm">
                  <FiCalendar className="text-gray-400" size={14} />
                  <div>
                    <p className="text-xs text-gray-500">Assigned</p>
                    <p className="text-xs font-medium">{formatDate(step.assignedAt)}</p>
                  </div>
                </div>
              )}
              {step.completedAt && (
                <div className="flex items-center gap-2 text-sm">
                  <FiCalendar className="text-gray-400" size={14} />
                  <div>
                    <p className="text-xs text-gray-500">Completed</p>
                    <p className="text-xs font-medium">{formatDate(step.completedAt)}</p>
                  </div>
                </div>
              )}
              {step.reviewedAt && (
                <div className="flex items-center gap-2 text-sm">
                  <FiCalendar className="text-gray-400" size={14} />
                  <div>
                    <p className="text-xs text-gray-500">Reviewed</p>
                    <p className="text-xs font-medium">{formatDate(step.reviewedAt)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Rating */}
            {step.rating && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Rating</p>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FiStar
                      key={star}
                      className={star <= step.rating! ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
                      size={16}
                    />
                  ))}
                  <span className="ml-2 text-sm text-gray-600">({step.rating}/5)</span>
                </div>
              </div>
            )}

            {/* Remarks */}
            {step.remarks && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Remarks</p>
                <div className="p-3 bg-gray-50 rounded text-sm text-gray-700">
                  {step.remarks}
                </div>
              </div>
            )}

            {/* Quality Feedback */}
            {step.qualityFeedback && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Quality Feedback</p>
                <div className="p-3 bg-blue-50 rounded text-sm text-gray-700">
                  {step.qualityFeedback}
                </div>
              </div>
            )}

            {step.rejectionReason && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Quality Feedback</p>
                <div className="p-3 bg-blue-50 rounded text-sm text-gray-700">
                  {step.rejectionReason}
                </div>
              </div>
            )}

            {/* Rejection Details */}
            {step.stepStatus === "rejected" && step.rejectionReason && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Rejection Reason</p>
                <div className="p-3 bg-red-50 rounded text-sm text-red-700">
                  {step.rejectionReason}
                </div>
              </div>
            )}

            {/* Files */}
            {step.files && Object.keys(step.files).length > 0 && (() => {
              // Aggregate all files from all groups
              const allFiles = Object.values(step.files).flat()
              const allImageFiles: Array<{ file: FileUpload; group: string }> = []
              const allVideoFiles: Array<{ file: FileUpload; group: string }> = []
              const allDocumentFiles: Array<{ file: FileUpload; group: string }> = []

              // Separate files by type and preserve group information
              Object.entries(step.files).forEach(([description, files]) => {
                files.forEach(file => {
                  if (isImageFile(file)) {
                    allImageFiles.push({ file, group: description })
                  } else if (isVideoFile(file)) {
                    allVideoFiles.push({ file, group: description })
                  } else {
                    allDocumentFiles.push({ file, group: description })
                  }
                })
              })

              // Set default tab only on initial load
              // Once user clicks a tab, respect their choice even if empty
              const defaultTab = allImageFiles.length > 0 ? 'images' : allVideoFiles.length > 0 ? 'videos' : 'documents'

              // Initialize default tab only once when files are first loaded
              if (!fileTabInitialized && Object.keys(step.files).length > 0) {
                setActiveFileTab(defaultTab)
                setFileTabInitialized(true)
              }

              const effectiveTab = activeFileTab

              // Group files by their original group for display
              const groupImages: Record<string, FileUpload[]> = {}
              allImageFiles.forEach(item => {
                if (!groupImages[item.group]) {
                  groupImages[item.group] = []
                }
                groupImages[item.group].push(item.file)
              })

              const groupVideos: Record<string, FileUpload[]> = {}
              allVideoFiles.forEach(item => {
                if (!groupVideos[item.group]) {
                  groupVideos[item.group] = []
                }
                groupVideos[item.group].push(item.file)
              })

              const groupDocuments: Record<string, FileUpload[]> = {}
              allDocumentFiles.forEach(item => {
                if (!groupDocuments[item.group]) {
                  groupDocuments[item.group] = []
                }
                groupDocuments[item.group].push(item.file)
              })

              const groupImageEntries = Object.entries(groupImages)
              const groupVideoEntries = Object.entries(groupVideos)
              const groupDocumentEntries = Object.entries(groupDocuments)
              const orderedImageFiles = groupImageEntries.flatMap(([, files]) => files)
              const orderedVideoFiles = groupVideoEntries.flatMap(([, files]) => files)

              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-500">
                      Files ({allFiles.length})
                    </p>

                    <div className="flex items-center gap-4">
        

                        <button
                          onClick={() => handleDownloadZip()}
                          disabled={isDownloadingZip}
                          className={`flex items-center gap-1 text-xs font-medium transition-colors ${isDownloadingZip ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                        >
                          {isDownloadingZip ? (
                            <>
                            <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                              {downloadingGroup && downloadingGroup !== "__all__" ? "Downloading Group..." : "Downloading..."}
                            </>
                          ) : (
                            <>
                              <FiDownload size={14} />
                            Download ZIP
                          </>
                        )}
                      </button>
                    </div>
                  </div>

             

                  {/* Single Tabs */}
                  <div className="flex gap-2 border-b border-gray-200 mb-4">
                    <button
                      onClick={() => setActiveFileTab('images')}
                      className={`px-3 py-2 text-xs font-medium transition-colors ${effectiveTab === 'images'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      Images ({allImageFiles.length})
                    </button>
                    <button
                      onClick={() => setActiveFileTab('videos')}
                      className={`px-3 py-2 text-xs font-medium transition-colors ${effectiveTab === 'videos'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      Videos ({allVideoFiles.length})
                    </button>
                    <button
                      onClick={() => setActiveFileTab('documents')}
                      className={`px-3 py-2 text-xs font-medium transition-colors ${effectiveTab === 'documents'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      Documents ({allDocumentFiles.length})
                    </button>
                  </div>

                  {/* Gallery Upload Options — visible based on specific permissions */}
                  {canShowUploadOptions && (
                    <div className="mb-4 space-y-3 border border-blue-100 bg-blue-50 rounded p-3">
                      <p className="text-xs font-semibold text-blue-800 flex items-center gap-1">
                        <FiUpload size={12} />
                        Upload Options
                      </p>

                      {/* Add More Files Option */}
                      {canAddMoreFiles && (
                        <div className="space-y-2 pb-3 border-b border-blue-200">
                          <p className="text-xs font-medium text-blue-900">Add More Files</p>
                          <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 items-center">
                            <select
                              value={correctionStage}
                              onChange={(e) => setCorrectionStage(e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded text-xs bg-white"
                            >
                              <option value="">Select upload stage</option>
                              <option value="data_uploaded">Site Data Upload</option>
                              <option value="field_work_completed">Field Work Completed</option>
                              <option value="ta_review_completed">TA Review</option>
                              <option value="report_maker_assigned">Report Maker</option>
                              {canCorrectReportFiles && (
                                <>
                                  <option value="report_completed">Interim Report Prepared</option>
                                  <option value="report_finalized">Report Finalized</option>
                                </>
                              )}
                            </select>
                            <input
                              type="file"
                              multiple
                              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.dwg"
                              onChange={(e) => setCorrectionFiles(Array.from(e.target.files || []))}
                              className="text-xs"
                            />
                            <button
                              type="button"
                              onClick={handleUploadCorrectionFiles}
                              disabled={!correctionStage || correctionFiles.length === 0 || correctionUploading}
                              className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-blue-800 text-white rounded text-xs disabled:bg-blue-300"
                            >
                              <FiUpload size={13} />
                              {correctionUploading ? "Uploading..." : "Upload"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Upload Others Option */}
                      {canUploadOthers && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-blue-900">Upload Others</p>
                          <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 items-center">
                            <select
                              value={correctionStage}
                              onChange={(e) => setCorrectionStage(e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded text-xs bg-white"
                            >
                              <option value="">Select upload stage</option>
                              <option value="data_uploaded">Site Data Upload</option>
                              <option value="field_work_completed">Field Work Completed</option>
                              <option value="ta_review_completed">TA Review</option>
                              <option value="report_maker_assigned">Report Maker</option>
                              {canCorrectReportFiles && (
                                <>
                                  <option value="report_completed">Interim Report Prepared</option>
                                  <option value="report_finalized">Report Finalized</option>
                                </>
                              )}
                            </select>
                            <input
                              type="file"
                              multiple
                              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.dwg"
                              onChange={(e) => setCorrectionFiles(Array.from(e.target.files || []))}
                              className="text-xs"
                            />
                            <button
                              type="button"
                              onClick={handleUploadCorrectionFiles}
                              disabled={!correctionStage || correctionFiles.length === 0 || correctionUploading}
                              className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-blue-800 text-white rounded text-xs disabled:bg-blue-300"
                            >
                              <FiUpload size={13} />
                              {correctionUploading ? "Uploading..." : "Upload"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Images Gallery */}
                  {effectiveTab === 'images' && orderedImageFiles.length > 0 && (
                      <div className="space-y-4">
                        {groupImageEntries.map(([description, files]) => (
                          <div key={description} className="space-y-2">
                            <div className="flex items-center justify-between gap-3 px-1">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                {description}
                              </p>
                              <button
                                type="button"
                                onClick={() => handleDownloadZip(description)}
                                disabled={isDownloadingZip}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                              >
                                <FiDownload size={12} />
                                {downloadingGroup === description ? "Downloading..." : "Download Group"}
                              </button>
                            </div>
                            <div className={`${isDataUploadView ? "max-h-[68vh]" : "max-h-[360px]"} overflow-y-auto pr-1`}>
                            <div className={`grid ${isDataUploadView ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"} gap-3`}>
                              {files.map((file) => (
                                <div
                                  key={file.id}
                                  className="relative group bg-white border border-gray-200 rounded overflow-hidden hover:border-blue-300 transition-all"
                                >
                                  <div
                                    onClick={() => handlePreviewFile(file, orderedImageFiles)}
                                    className="aspect-square bg-gray-100 cursor-pointer relative"
                                  >
                                    <img
                                      src={file.file_path}
                                      alt={file.file_name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement
                                        target.style.display = 'none'
                                        if (target.parentElement) {
                                          target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gray-100"><svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>'
                                        }
                                      }}
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                                      <FiEye className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                                    </div>
                                  </div>
                                  <div className="p-2">
                                    <p className="text-xs font-medium truncate" title={file.file_name}>
                                      {file.file_name}
                                    </p>
                                    <p className="text-[10px] text-gray-500">
                                      {formatFileSize(Number(file.file_size))} • {formatDate(file.uploaded_at)}
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDownloadFile(file.file_path, file.file_name)
                                    }}
                                    className="absolute top-2 right-2 p-1.5 bg-white rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
                                    title="Download"
                                  >
                                    <FiDownload className="text-gray-600" size={14} />
                                  </button>
                                  {(canAddMoreFiles || canUploadOthers) && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteReportFile(file)
                                      }}
                                      className="absolute top-2 left-2 p-1.5 bg-white rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                                      title="Remove"
                                    >
                                      <FiTrash2 className="text-red-600" size={14} />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Videos Gallery */}
                  {effectiveTab === 'videos' && orderedVideoFiles.length > 0 && (
                      <div className="space-y-4">
                        {groupVideoEntries.map(([description, files]) => (
                          <div key={description} className="space-y-2">
                            <div className="flex items-center justify-between gap-3 px-1">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                {description}
                              </p>
                              <button
                                type="button"
                                onClick={() => handleDownloadZip(description)}
                                disabled={isDownloadingZip}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                              >
                                <FiDownload size={12} />
                                {downloadingGroup === description ? "Downloading..." : "Download Group"}
                              </button>
                            </div>
                            <div className={`${isDataUploadView ? "max-h-[68vh]" : "max-h-[360px]"} overflow-y-auto pr-1`}>
                            <div className={`grid ${isDataUploadView ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"} gap-3`}>
                              {files.map((file) => (
                                <div
                                  key={file.id}
                                  className="relative group bg-white border border-gray-200 rounded overflow-hidden hover:border-blue-300 transition-all"
                                >
                                  <div
                                    onClick={() => handlePreviewFile(file, orderedVideoFiles)}
                                    className="aspect-square bg-gray-900 cursor-pointer relative"
                                  >
                                    <Video
                                      src={file.file_path}
                                      preload="metadata"
                                      muted
                                      playsInline
                                      className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                                      <FiEye className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                                    </div>
                                  </div>
                                  <div className="p-2">
                                    <p className="text-xs font-medium truncate" title={file.file_name}>
                                      {file.file_name}
                                    </p>
                                    <p className="text-[10px] text-gray-500">
                                      {formatFileSize(Number(file.file_size))} • {formatDate(file.uploaded_at)}
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDownloadFile(file.file_path, file.file_name)
                                    }}
                                    className="absolute top-2 right-2 p-1.5 bg-white rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
                                    title="Download"
                                  >
                                    <FiDownload className="text-gray-600" size={14} />
                                  </button>
                                  {(canAddMoreFiles || canUploadOthers) && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteReportFile(file)
                                      }}
                                      className="absolute top-2 left-2 p-1.5 bg-white rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                                      title="Remove"
                                    >
                                      <FiTrash2 className="text-red-600" size={14} />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Documents List */}
                  {effectiveTab === 'documents' && allDocumentFiles.length > 0 && (
                      <div className="space-y-4">
                        {groupDocumentEntries.map(([description, files]) => (
                          <div key={description} className="space-y-2">
                            <div className="flex items-center justify-between gap-3 px-1">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                {description}
                              </p>
                              <button
                                type="button"
                                onClick={() => handleDownloadZip(description)}
                                disabled={isDownloadingZip}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                              >
                                <FiDownload size={12} />
                                {downloadingGroup === description ? "Downloading..." : "Download Group"}
                              </button>
                            </div>
                            <div className="space-y-2">
                            {files.map((file) => (
                              <div
                                key={file.id}
                                className="flex items-center gap-3 p-2 bg-white border border-gray-200 rounded hover:border-blue-300 hover:bg-blue-50 transition-all"
                              >
                                <div className="flex items-center justify-center w-8 h-8 rounded bg-gray-50">
                                  {getFileTypeIcon(file)}
                                </div>
                                <div
                                  onClick={() => handlePreviewFile(file)}
                                  className="flex-1 min-w-0 cursor-pointer"
                                >
                                  <p className="text-sm font-medium truncate">{file.file_name}</p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(Number(file.file_size))} • {formatDate(file.uploaded_at)}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDownloadFile(file.file_path, file.file_name)
                                  }}
                                  className="p-1 rounded hover:bg-gray-100 text-gray-500"
                                  title="Download"
                                >
                                  <FiDownload className="text-gray-400" size={14} />
                                </button>
                                {(canAddMoreFiles || canUploadOthers) && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDeleteReportFile(file)
                                    }}
                                    className="p-1 rounded hover:bg-red-50 text-red-600"
                                    title="Remove"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Empty States */}
                  {effectiveTab === 'images' && allImageFiles.length === 0 && (
                    <div className="text-center py-12">
                      <FiImage className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-500">No images available</p>
                      <p className="text-xs text-gray-400 mt-1">There are no image files in this workflow step</p>
                    </div>
                  )}
                  {effectiveTab === 'videos' && allVideoFiles.length === 0 && (
                    <div className="text-center py-12">
                      {/* <FiVideo className="w-16 h-16 text-gray-300 mx-auto mb-3" /> */}
                      <p className="text-sm font-medium text-gray-500">No videos available</p>
                      <p className="text-xs text-gray-400 mt-1">There are no video files in this workflow step</p>
                    </div>
                  )}
                  {effectiveTab === 'documents' && allDocumentFiles.length === 0 && (
                    <div className="text-center py-12">
                      <FiFileText className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-500">No documents available</p>
                      <p className="text-xs text-gray-400 mt-1">There are no document files in this workflow step</p>
                    </div>
                  )}
                </div>
              )
            })()}

          </div>
        )}
      </div>
    )
  }

  if (!isOpen) return null

  const requestedStep = apiData?.requested_step
  const previousSteps = apiData?.previous_steps || []
  const summary = apiData?.summary
  const isDataUploadView = status === "data_uploaded"

  const isPreviewImage = previewFile ? isImageFile(previewFile) : false
  const isPreviewVideo = previewFile ? isVideoFile(previewFile) : false

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />

      {/* Offcanvas */}
      <div className={`fixed top-0 right-0 h-full w-full ${isDataUploadView ? "max-w-7xl" : "max-w-5xl"} bg-white shadow-xl z-50`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Workflow Step Details</h2>
            <p className="text-sm text-gray-600 mt-1">
              {apiData?.site_info?.site_name} • {apiData?.site_info?.site_id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-50 rounded transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="h-[calc(100%-88px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : apiData ? (
            <div className="p-6">
              {/* Summary Stats */}
              {summary && (
                <div className="grid grid-cols-4 gap-3 mb-6">
                  <div className="p-3 bg-blue-50 rounded border border-blue-100">
                    <p className="text-xs text-blue-600 font-medium">Total Steps</p>
                    <p className="text-xl font-bold text-blue-900">{summary.total_steps}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded border border-green-100">
                    <p className="text-xs text-green-600 font-medium">Completed</p>
                    <p className="text-xl font-bold text-green-900">{summary.completed_steps}</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded border border-yellow-100">
                    <p className="text-xs text-yellow-600 font-medium">In Progress</p>
                    <p className="text-xl font-bold text-yellow-900">
                      {summary.in_progress_steps + summary.pending_steps}
                    </p>
                  </div>
                  <div className="p-3 bg-red-50 rounded border border-red-100">
                    <p className="text-xs text-red-600 font-medium">Rejections</p>
                    <p className="text-xl font-bold text-red-900">{summary.total_rejections}</p>
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setActiveTab('current')}
                  className={`px-4 py-2 rounded font-medium text-sm transition-colors ${activeTab === 'current'
                    ? 'bg-blue-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Current Step
                </button>
                <button
                  onClick={() => setActiveTab('previous')}
                  className={`px-4 py-2 rounded font-medium text-sm transition-colors ${activeTab === 'previous'
                    ? 'bg-blue-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Previous Steps ({previousSteps.length})
                </button>
              </div>

              {/* Content based on tab */}
              <div className="space-y-4">
                {activeTab === 'current' && requestedStep ? (
                  renderStepCard(requestedStep, 0, true)
                ) : activeTab === 'previous' ? (
                  previousSteps.length > 0 ? (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        View all previous workflow steps leading to the current status
                      </p>
                      {previousSteps.map((step, index) => renderStepCard(step, index))}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No previous steps available
                    </div>
                  )
                ) : null}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <FiAlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No details available</p>
            </div>
          )}
        </div>
      </div>

      {/* File Preview Modal for images/videos */}
      {previewFile && (isPreviewImage || isPreviewVideo) && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 100 }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-60" onClick={closePreview} />
          <div ref={previewContainerRef} className={`relative bg-white rounded-lg shadow-2xl ${isDataUploadView ? "max-w-6xl" : "max-w-4xl"} w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{previewFile.file_name}</p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(Number(previewFile.file_size))} • {formatDate(previewFile.uploaded_at)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isPreviewImage && (
                  <>
                    <button
                      type="button"
                      onClick={handleZoomOut}
                      className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                      disabled={imageZoom <= MIN_IMAGE_ZOOM}
                      title="Zoom out"
                    >
                      -
                    </button>
                    <span className="w-16 text-center text-xs text-gray-600">
                      {Math.round(imageZoom * 100)}%
                    </span>
                    <button
                      type="button"
                      onClick={handleZoomIn}
                      className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                      disabled={imageZoom >= MAX_IMAGE_ZOOM}
                      title="Zoom in"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={handleRotate}
                      className="p-2 rounded hover:bg-gray-100 text-gray-500"
                      title="Rotate 90°"
                    >
                      <FiRotateCw className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => window.open(previewFile.file_path, '_blank')}
                  className="p-2 rounded hover:bg-gray-100 text-gray-500"
                  title="Open in new tab"
                >
                  <FiExternalLink className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={togglePreviewFullscreen}
                  className="p-2 rounded hover:bg-gray-100 text-gray-500"
                  title={isPreviewFullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                  {isPreviewFullscreen ? <FiMinimize2 className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadFile(previewFile.file_path, previewFile.file_name)}
                  className="p-2 rounded hover:bg-gray-100 text-gray-500"
                  title="Download"
                >
                  <FiDownload className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={closePreview}
                  className="p-2 rounded hover:bg-gray-100 text-gray-500"
                  title="Close"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 bg-black flex items-center justify-center overflow-auto p-4">
              {isPreviewImage && (
                <img
                  src={previewFile.file_path}
                  alt={previewFile.file_name}
                  className="max-h-[80vh] max-w-full object-contain transition-transform duration-200"
                  style={{ transform: `scale(${imageZoom}) rotate(${imageRotation}deg)` }}
                  onDoubleClick={handleImageDoubleClick}
                  title="Double-click to zoom in/out"
                />
              )}
              {isPreviewVideo && (
                <Video
                  src={previewFile.file_path}
                  controls
                  className="max-h-[80vh] max-w-full"
                />
              )}
            </div>

            {previewCollection.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => handlePreviewNavigation("prev")}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-700 rounded-full p-2 shadow focus:outline-none transition"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => handlePreviewNavigation("next")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-700 rounded-full p-2 shadow focus:outline-none transition"
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
